import React, { useEffect, useState } from 'react';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { buildDatasetJsonLd } from '../../../utils/schemaOrgHelpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faTools,
  faChevronDown,
  faExternalLinkAlt,
  faDownload,
  faMapMarkerAlt,
  faEye,
  faCommentDots,
  faGlobe,
} from '@fortawesome/free-solid-svg-icons';
import {
  publishOpenChatModal,
  subscribeChatButtonConfig,
} from '../VoyagerEnhancedSearch/utils/eventBus';
import DetailsTab from './components/DetailsTab/DetailsTab';
import RelationshipsTab from './components/RelationshipsTab/RelationshipsTab';
import { useDetailItem } from './hooks/useDetailItem';
import {
  buildAgolUrl,
  isAgolEligibleFormat,
  parseAgolEligibleFormats,
} from './constants/agolConfig';
import { logDebug } from '../../../utils/logger';
import { resolveBlockImageSrc } from './utils/blockImage';
import type { BlockImageItem } from './utils/blockImage';
import './VoyagerSearchDetailsPageBlock.scss';

// Interface for detail item from Voyager API
interface VoyagerDetailItem {
  id: string;
  name?: string;
  title?: string;
  format?: string;
  format_type?: string;
  format_category?: string;
  format_company?: string;
  abstract?: string;
  description?: string;
  fullpath?: string;
  absolute_path?: string;
  thumb?: string;
  path_to_thumb?: string;
  preview?: string;
  download?: string;
  content?: string;
  serviceUrl?: string;
  modified?: string;
  bbox?: string;
  area?: number;
  geo?: {
    type: string;
    coordinates: number[] | number[][] | number[][][];
  };
  geo_full?: any;
  keywords?: string[];
  tag_tags?: string[];
  likes?: number;
  dislikes?: number;
  bytes?: number;
  views?: number;
  interactions?: number;
  // Acquisition/Platform fields
  fd__acquired?: string;
  fd_acquired?: string;
  created?: string;
  indexed?: string;
  fd__published?: string;
  fd__updated?: string;
  fs_platform?: string;
  fs__provider?: string;
  fs__satellite_id?: string;
  fs__instrument?: string;
  fs__item_type?: string;
  fs__strip_id?: string;
  fs__publishing_stage?: string;
  fs__quality_category?: string;
  fs_source?: string;
  fs_source_location?: string;
  fs_vendor?: string;
  fs_thumbnail_url?: string;
  // Additional metadata fields
  fi__anomalous_pixels?: number;
  fi__clear_confidence_percent?: number;
  fi__clear_percent?: number;
  fi__cloud_cover?: number;
  fi__cloud_percent?: number;
  fb__ground_control?: boolean;
  fi__gsd?: number;
  fi__heavy_haze_percent?: number;
  fi__light_haze_percent?: number;
  fi__pixel_resolution?: number;
  fi__satellite_azimuth?: number;
  fi__shadow_percent?: number;
  fi__snow_ice_percent?: number;
  fu__sun_azimuth?: number;
  fu__sun_elevation?: number;
  fu__view_angle?: number;
  fi__visible_confidence_percent?: number;
  fi__visible_percent?: number;
  // Group fields
  grp_Agency?: string[];
  grp_Organization?: string[];
  grp_Sector?: string[];
  grp_Data_Theme?: string[];
  grp_Catalog?: string[];
  grp_Collection?: string[];
  grp_data_source?: string[];
  grp_Payment_Required?: string[];
  // Agent/Worker fields
  agent?: string;
  worker?: string;
  extractor?: string;
  fs_agent_extract?: string;
  has_spatial?: boolean;
  hasMetadata?: boolean;
  hasPreview?: boolean;
  hasThumb?: boolean;
  location?: string;
  properties?: string[];
  // Any other dynamic fields
  [key: string]: any;
}

interface VoyagerSearchDetailsPageBlockProps {
  data?: {
    itemId?: string;
    returnUrl?: string;
    voyagerBaseUrl?: string;
    voyagerConfigId?: string;
    // Per-page branding; unset falls back to the site-wide images.
    thumbnailImage?: BlockImageItem[];
    headerBanner?: BlockImageItem[];
    agolEligibleFormats?: string;
    // AGOL base URL, per page. Was read from the Data Registry Settings page,
    // which no longer exists - each search page now configures its own.
    arcgisUrl?: string;
  };
}

const HTML_TAG_REGEX = /<\/?[a-z][\s\S]*>/i;

const sanitizeRichText = (value: string): string => {
  return value
    .replace(
      /<\s*(script|style|iframe|object|embed|link|meta|base)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi,
      '',
    )
    .replace(
      /<\s*(script|style|iframe|object|embed|link|meta|base)[^>]*\/?>/gi,
      '',
    )
    .replace(/\son[a-z]+\s*=\s*(['"]).*?\1/gi, '')
    .replace(/\son[a-z]+\s*=\s*[^\s>]+/gi, '')
    .replace(/\s(href|src)\s*=\s*(['"])\s*javascript:[\s\S]*?\2/gi, '');
};

const getDescriptionContent = (
  item: VoyagerDetailItem,
): { value: string; isRichText: boolean } | null => {
  const description = String(item.abstract || item.description || '').trim();
  if (!description) {
    return null;
  }

  const isRichText = HTML_TAG_REGEX.test(description);
  return {
    value: isRichText ? sanitizeRichText(description) : description,
    isRichText,
  };
};

const getDetailDocumentUrl = (item: VoyagerDetailItem): string => {
  const candidateUrls = [
    item.fullpath,
    item.absolute_path,
    item.download,
    item.content,
    item.serviceUrl,
    item.path,
  ];

  const pdfUrl = candidateUrls.find((url) =>
    String(url || '')
      .toLowerCase()
      .includes('.pdf'),
  );

  return pdfUrl || item.fullpath || item.absolute_path || item.download || '';
};

const hasPdfContent = (item: VoyagerDetailItem): boolean => {
  const candidateValues = [
    item.format,
    item.format_type,
    item.format_category,
    item.content,
    item.fullpath,
    item.absolute_path,
    item.download,
    item.serviceUrl,
  ];

  return candidateValues.some((value) =>
    String(value || '')
      .toLowerCase()
      .includes('pdf'),
  );
};

const VoyagerSearchDetailsPageBlock: React.FC<
  VoyagerSearchDetailsPageBlockProps
> = ({ data }) => {
  const { item, isLoading } = useDetailItem(data);
  const detailsHeaderBannerSrc = resolveBlockImageSrc(data?.headerBanner);
  const detailsLogoSrc = resolveBlockImageSrc(data?.thumbnailImage);
  const agolBaseUrl = data?.arcgisUrl || '';
  const descriptionContent = item ? getDescriptionContent(item) : null;

  const [activeTab, setActiveTab] = useState<
    'details' | 'relationships' | 'metadata'
  >('details');
  const [chatWithPdfButtonText, setChatWithPdfButtonText] = useState<
    string | null
  >(null);
  const [toolsDropdownOpen, setToolsDropdownOpen] = useState(false);
  const [MapComponent, setMapComponent] =
    useState<React.ComponentType<any> | null>(null);

  // Dynamically load map component
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const loadMapComponent = async () => {
      try {
        const { default: DetailsMapView } = await import(
          './components/DetailsMapView/DetailsMapView'
        );
        setMapComponent(() => DetailsMapView);
      } catch (err) {
        logDebug('Map component not available, using fallback');
      }
    };

    loadMapComponent();
  }, []);

  useEffect(
    () =>
      subscribeChatButtonConfig((config) => {
        setChatWithPdfButtonText(config?.chatWithPdfButtonText || null);
      }),
    [],
  );

  // Navigate back to search
  const handleReturnToSearch = () => {
    if (data?.returnUrl) {
      window.location.href = data.returnUrl;
    } else if (typeof window !== 'undefined') {
      window.history.back();
    }
  };

  const handleChatWithPdfClick = () => {
    if (!item) return;

    publishOpenChatModal({
      documentTitle: item.title || item.name || item.id,
      documentUrl: getDetailDocumentUrl(item),
      source: 'voyager-details-page',
      uuid: item.id,
    });
  };

  const agolEligibleFormats = parseAgolEligibleFormats(
    data?.agolEligibleFormats,
  );
  const datasetLink = item ? item.fullpath || item.absolute_path || '' : '';
  const canOpenInAgol =
    !!item &&
    !!agolBaseUrl &&
    !!datasetLink &&
    isAgolEligibleFormat([item.format, item.format_type], agolEligibleFormats);

  const handleOpenInAgolClick = () => {
    if (!canOpenInAgol) return;
    window.open(
      buildAgolUrl(agolBaseUrl, datasetLink),
      '_blank',
      'noopener,noreferrer',
    );
  };

  if (isLoading) {
    return (
      <div className="voyager-details-page">
        <div className="details-loading">
          <div className="loading-spinner"></div>
          <p>Loading item details...</p>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="voyager-details-page">
        <div className="details-error">
          <p>Item not found</p>
          <button className="btn-return" onClick={handleReturnToSearch}>
            Return to Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="voyager-details-page">
      <Helmet
        script={[
          {
            type: 'application/ld+json',
            innerHTML: JSON.stringify(buildDatasetJsonLd(item)),
          },
        ]}
      />
      <div className="details-top-section">
        {detailsHeaderBannerSrc && (
          <img
            src={detailsHeaderBannerSrc}
            alt=""
            className="details-top-section__hero-bg"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
        <div className="details-top-section__content">
          <div className="site-search-row">
            {detailsLogoSrc && (
              <img
                src={detailsLogoSrc}
                alt=""
                className="site-search-row__logo"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
          </div>
        </div>
      </div>
      {/* Header */}
      <header className="details-header">
        <button className="btn-return-link" onClick={handleReturnToSearch}>
          <FontAwesomeIcon icon={faArrowLeft} className="icon-left" />
          Return to Search
        </button>

        <div className="header-actions">
          {/* Tools Dropdown */}
          <div className="dropdown-container">
            <button
              className="btn-tools"
              onClick={() => setToolsDropdownOpen((open) => !open)}
            >
              <FontAwesomeIcon icon={faTools} />
              Tools
              <FontAwesomeIcon icon={faChevronDown} className="dropdown-icon" />
            </button>
            {toolsDropdownOpen && (
              <div className="dropdown-menu">
                <button className="dropdown-item">
                  <FontAwesomeIcon icon={faDownload} />
                  Download
                </button>
                <button className="dropdown-item">
                  <FontAwesomeIcon icon={faEye} />
                  Preview
                </button>
                <button className="dropdown-item">
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                  View on Map
                </button>
                {canOpenInAgol && (
                  <button
                    className="dropdown-item"
                    onClick={handleOpenInAgolClick}
                  >
                    <FontAwesomeIcon icon={faGlobe} />
                    Open in AGOL
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Dataset Summary */}
      <section className="details-summary">
        <h1 className="item-title">{item.title || item.name || item.id}</h1>

        {descriptionContent && (
          <div className="summary-description">
            <h2>Description</h2>
            {descriptionContent.isRichText ? (
              <div
                className="description-rich-text"
                dangerouslySetInnerHTML={{ __html: descriptionContent.value }}
              />
            ) : (
              <p>{descriptionContent.value}</p>
            )}
          </div>
        )}

        {chatWithPdfButtonText &&
        hasPdfContent(item) &&
        getDetailDocumentUrl(item) ? (
          <div className="summary-actions">
            <button
              className="btn-chat-pdf"
              type="button"
              onClick={handleChatWithPdfClick}
            >
              <FontAwesomeIcon icon={faCommentDots} />
              {chatWithPdfButtonText}
            </button>
          </div>
        ) : null}

        <div className="summary-meta">
          <div className="item-format">
            <span className="format-link">
              {item.format || item.format_type || 'Unknown Format'}
            </span>
          </div>

          <div className="item-path">
            <a
              href={item.fullpath || item.absolute_path || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="path-link"
            >
              {item.fullpath || item.absolute_path || 'No path available'}
              <FontAwesomeIcon
                icon={faExternalLinkAlt}
                className="external-icon"
              />
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="details-content">
        <div className="details-section">
          <div className="details-tabs">
            <button
              className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Details
            </button>
            <button
              className={`tab-btn ${activeTab === 'relationships' ? 'active' : ''}`}
              onClick={() => setActiveTab('relationships')}
            >
              Relationships
            </button>
            <button
              className={`tab-btn ${activeTab === 'metadata' ? 'active' : ''}`}
              onClick={() => setActiveTab('metadata')}
            >
              Metadata
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'details' && <DetailsTab item={item} />}
            {activeTab === 'relationships' && <RelationshipsTab item={item} />}
            {activeTab === 'metadata' && (
              <div className="metadata-raw">
                <pre>{JSON.stringify(item, null, 2)}</pre>
              </div>
            )}
          </div>
        </div>

        <div className="details-right-column">
          <div className="details-map">
            {MapComponent && (item.geo || item.bbox) ? (
              <MapComponent item={item} />
            ) : item.bbox ? (
              <div className="map-placeholder">
                <div className="bbox-display">
                  <span>Bounding Box:</span>
                  <code>{item.bbox}</code>
                </div>
              </div>
            ) : (
              <div className="map-placeholder">
                <span>No geographic data available</span>
              </div>
            )}
          </div>
        </div>
      </main>

      {item.keywords && item.keywords.length > 0 && (
        <section className="details-keywords">
          <h2>Keywords</h2>
          <div className="keywords-list">
            {item.keywords.map((keyword, index) => (
              <span key={index} className="keyword-tag">
                {keyword}
              </span>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default VoyagerSearchDetailsPageBlock;
